import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  type?: "website" | "article";
  image?: string;
  noIndex?: boolean;
}

const siteUrl = "https://alpacaboerderijdordrecht.nl";
const defaultImagePath = "/assets/hero-alpacas.webp";
const defaultImageUrl = `${siteUrl}${defaultImagePath}`;
const defaultImageDimensions = {
  width: "1600",
  height: "1066",
};

const SEOHead = ({
  title,
  description,
  canonical,
  type = "website",
  image = defaultImagePath,
  noIndex = false,
}: SEOHeadProps) => {
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const fullImage = image.startsWith("http")
    ? image
    : `${siteUrl}${image.startsWith("/") ? image : `/${image}`}`;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Helper to update or create meta tags
    const updateMeta = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    const removeMeta = (name: string, property = false) => {
      const attr = property ? "property" : "name";
      document.querySelector(`meta[${attr}="${name}"]`)?.remove();
    };

    // Helper to update or create link tags
    const updateLink = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement("link");
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    // Basic meta tags
    updateMeta("description", description);
    updateMeta("robots", noIndex ? "noindex, nofollow" : "index, follow");

    // Open Graph
    updateMeta("og:title", title, true);
    updateMeta("og:description", description, true);
    updateMeta("og:type", type, true);
    updateMeta("og:url", fullCanonical, true);
    updateMeta("og:image", fullImage, true);
    if (fullImage === defaultImageUrl) {
      updateMeta("og:image:width", defaultImageDimensions.width, true);
      updateMeta("og:image:height", defaultImageDimensions.height, true);
    } else {
      removeMeta("og:image:width", true);
      removeMeta("og:image:height", true);
    }
    updateMeta("og:site_name", "Alpacaboerderij Dordrecht", true);
    updateMeta("og:locale", "nl_NL", true);

    // Twitter Card
    updateMeta("twitter:card", "summary_large_image");
    updateMeta("twitter:title", title);
    updateMeta("twitter:description", description);
    updateMeta("twitter:image", fullImage);

    // Canonical link
    updateLink("canonical", fullCanonical);

    // Cleanup function not needed as we want meta tags to persist
  }, [title, description, fullCanonical, fullImage, type, noIndex]);

  return null;
};

export default SEOHead;
